import { addToRecord, updateRecord } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type {
  UserFollowedUsersLoadAction,
  UserFollowedUsersLoadFulfilledAction,
  UserFollowedUsersLoadPendingAction,
} from '../../domains/userFollowedUsers/actions.types';
import type { UserHeaderLoadPendingAction } from '../../domains/userHeaderLoad/actions.types';
import type { User } from '../users/types';

import type { UserFollowedUsers, UsersFollowedUsers } from './types';

export type { UserFollowedUsersLoadAction };

const initialState: UsersFollowedUsers = {};

const initialUserFollowedUsers: UserFollowedUsers = {
  id: '',
  followedUsers: [],
  cursor: null,
  hasNextPage: false,
  totalCount: 0,
  status: 'notRequested',
};

const stateWithPendingFollowedUsers = (
  state: UsersFollowedUsers,
  { id, isInitialLoad }: UserFollowedUsersLoadPendingAction['payload']
): UsersFollowedUsers => {
  const followedUsers = isInitialLoad ? initialUserFollowedUsers : state[id];

  return addToRecord(state, id, { ...followedUsers, status: 'pending' });
};

const stateWithFulfilledFollowedUsers = (
  state: UsersFollowedUsers,
  { id, entities, isInitialLoad }: UserFollowedUsersLoadFulfilledAction['payload']
): UsersFollowedUsers => {
  const { cursor, hasNextPage, totalCount, followedUsers: newFollowedUsers } = entities.followedUsers[id];
  const followedUsers = isInitialLoad ? newFollowedUsers : [...state[id].followedUsers, ...newFollowedUsers];

  const fulfilledFollowedUsers: UserFollowedUsers = {
    id,
    followedUsers: [...new Set(followedUsers)],
    cursor,
    hasNextPage,
    totalCount,
    status: 'fulfilled',
  };

  return addToRecord(state, id, fulfilledFollowedUsers);
};

const stateWithRejectedFollowedUsers = (state: UsersFollowedUsers, id: User['id']): UsersFollowedUsers =>
  updateRecord(state, id, { status: 'rejected' });

const clearFollowedUsers = (
  state: UsersFollowedUsers,
  { id }: UserHeaderLoadPendingAction['payload']
): UsersFollowedUsers => {
  const followedUsers = initialUserFollowedUsers;

  // oxlint-disable-next-line @ms/no-unnecessary-spread
  return addToRecord(state, id, { ...followedUsers });
};

const reducer: Reducer<UsersFollowedUsers, UserFollowedUsersLoadAction | UserHeaderLoadPendingAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case 'USER_HEADER.LOAD_PENDING':
      return clearFollowedUsers(state, action.payload);
    case 'USER_FOLLOWED_USERS.LOAD_PENDING':
      return stateWithPendingFollowedUsers(state, action.payload);
    case 'USER_FOLLOWED_USERS.LOAD_FULFILLED':
      return stateWithFulfilledFollowedUsers(state, action.payload);
    case 'USER_FOLLOWED_USERS.LOAD_REJECTED':
      return stateWithRejectedFollowedUsers(state, action.payload.id);
    default:
      return state;
  }
};

export default reducer;
